/* eslint-disable no-param-reassign */
import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import DefaultApiParameters from '../../enum/DefaultApiParameters';
import {
  APIConsumerChargeCommercialResponseInterface,
  ConsumerChargeCommercialInitialProperties,
  RequestConsumerChargeCommercialInterface,
  ConsumerChargeCommercialInterface,
} from '../../interface/end-consumer-charge-Session-commercial-interface';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import endConsumerApiV2 from '../../utils/api/end-consumer-v2';

import { setNotificationMessage } from './NotificationManagementSlice';

export const fetchConsumerChargeCommercial = createAsyncThunk<
  APIConsumerChargeCommercialResponseInterface,
  RequestConsumerChargeCommercialInterface
>(
  'end-consumers/fetchConsumerChargeCommercial',
  async (data: RequestConsumerChargeCommercialInterface, { dispatch }) => {
    const { userCustomId, ...query } = data;
    if (!userCustomId) {
      throw new Error('userCustomId is required');
    }
    const response = await endConsumerApiV2
      .fetchChargeSessionList(userCustomId, removeEmptyKeys(query))
      .then((response1) => response1)
      .catch((error) =>
        dispatch(
          setNotificationMessage({
            type: 'error',
            message: error?.response?.data?.message,
            code: error?.response?.data?.code,
            status: true,
          }),
        ),
      );
    return {
      ...response.data,
      results: response.data.results.map((consumerData: ConsumerChargeCommercialInterface) => ({
        ...consumerData,
        paymentMethod:
          consumerData?.paymentMethod === 'hyperpay' ? 'Credit Card' : consumerData?.paymentMethod,
        chargingPointSn: consumerData?.chargingPoint?.chargingPointSn,
        chargingPointName: consumerData?.chargingPoint?.chargingPointName,
        chargingStationName: consumerData?.chargingStation?.name,
        chargingStationId: consumerData?.chargingStation?._id,
        vehicleName: consumerData?.vehicle?.manufacturer,
        vehicleModel: consumerData?.vehicle?.model,
        paymentStatus: consumerData?.walletHistoryObject?.paymentStatus,
        amount: consumerData?.walletHistoryObject?.amount || 0,
        hyperpayTransactionId: consumerData?.hyperpayTransaction?.hyperpayTransactionId,
        hyperpayTransactionIdStatus: consumerData?.hyperpayTransaction?.status,
      })),
    };
  },
);

export const fetchConsumerChargeCommercialFilter = createAsyncThunk<CommonFilterProperties[], void>(
  'end-consumers/fetchConsumerChargeCommercialFilter',
  async (_, { dispatch, getState }) => {
    const state: any = getState();
    const { selectedEndConsumer } = state.endConsumerType;

    if (!selectedEndConsumer?.userCustomId) {
      throw new Error('accountCustomId is required');
    }
    try {
      const response = await endConsumerApiV2.fetchChargeSessionCommercialFilters(
        selectedEndConsumer.userCustomId,
      );
      const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
      return updatedResponse;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationMessage',
        payload: {
          type: 'error',
          message: error?.response?.data?.message || 200,
          code: error?.response?.data?.code || 'Something went wrong!',
          status: true,
        },
      });
      throw error;
    }
  },
);

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

// Initial state
const initialState: ConsumerChargeCommercialInitialProperties = {
  consumers: [],
  limit: DefaultApiParameters.limit,
  page: DefaultApiParameters.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filter: {},
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: '',
  sortField: '',
};

export const ConsumerChargeCommercial = createSlice({
  name: 'end-consumers-charge-commercial',
  initialState,
  reducers: {
    /**
     * Update By month filters
     * @param state
     * @param action
     */
    updateConsumerChargeCommercialListFilter: (
      state,
      action: PayloadAction<FilterUpdateProperties>,
    ) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
      }
    },
    /**
     * function for set sortBy
     * @param state
     * @param action
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Use the extra reducers function
   * @param builder
   */
  extraReducers: (builder: ActionReducerMapBuilder<ConsumerChargeCommercialInitialProperties>) => {
    builder
      .addCase(
        fetchConsumerChargeCommercial.pending,
        (state: ConsumerChargeCommercialInitialProperties) => {
          state.loading = true;
        },
      )
      .addCase(
        fetchConsumerChargeCommercial.fulfilled,
        (
          state: ConsumerChargeCommercialInitialProperties,
          action: PayloadAction<APIConsumerChargeCommercialResponseInterface>,
        ) => {
          state.loading = false;
          state.consumers = [...action.payload.results];
          state.limit = action.payload.limit;
          state.page = action.payload.page;
          state.totalPages = action.payload.totalPages;
          state.totalResults = action.payload.totalResults;
        },
      )
      .addCase(
        fetchConsumerChargeCommercial.rejected,
        (state: ConsumerChargeCommercialInitialProperties) => {
          state.loading = false;
          // Handle error if needed
        },
      );

    builder
      .addCase(fetchConsumerChargeCommercialFilter.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchConsumerChargeCommercialFilter.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload];
        },
      )
      .addCase(fetchConsumerChargeCommercialFilter.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

// Export actions
export const { updateConsumerChargeCommercialListFilter, setSortData } =
  ConsumerChargeCommercial.actions;
// Export the reducer
export default ConsumerChargeCommercial.reducer;
